import { type ImageToken, type PixelRatio } from '@1debit/chime-assets';
import { forwardRef } from 'react';

import { getImageByToken } from '../utils/assets';
import { classNames } from '../utils/classNames';

export type RemoteImageProps = Omit<JSX.IntrinsicElements['img'], 'src' | 'width' | 'height' | 'alt' | 'children'> & {
	token: ImageToken;
	alt: string;
	width?: number | `${number}`;
	height?: number | `${number}`;
};

const pixelRatios: PixelRatio[] = [1, 2, 3];
const DEFAULT_PIXEL_RATIO: PixelRatio = 3;

type Image = ReturnType<typeof getImageByToken>;

const generateSrcSet = (image: NonNullable<Image>) => {
	const remainingPixelRatios = pixelRatios.filter(ratio => ratio != DEFAULT_PIXEL_RATIO);

	const lightModeSrcSetEntries = remainingPixelRatios
		.map(ratio => {
			const imageUri = image.uri;
			return imageUri ? `${imageUri} ${ratio}x` : undefined;
		})
		.filter(Boolean);

	const darkModeSrcSetEntries = remainingPixelRatios
		.map(ratio => {
			const imageUri = image.darkModeUri;
			return imageUri ? `${imageUri} ${ratio}x` : undefined;
		})
		.filter(Boolean);

	const lightModeSrcSet = `${lightModeSrcSetEntries.join(',')}, ${image.uri} ${DEFAULT_PIXEL_RATIO}x`;
	const darkModeSrcSet = `${darkModeSrcSetEntries.join(',')}, ${image.darkModeUri} ${DEFAULT_PIXEL_RATIO}x`;

	return {
		lightModeSrcSet,
		darkModeSrcSet,
	};
};

export const RemoteImage = forwardRef<HTMLImageElement, RemoteImageProps>(
	({ token, width, height, alt, loading = 'lazy', className, ...rest }, ref) => {
		const image = getImageByToken(token);
		if (!image) return null;

		const aspectRatio = image.width / image.height;
		let resolvedWidth = typeof width === 'string' ? parseFloat(width) : width;
		let resolvedHeight = typeof height === 'string' ? parseFloat(height) : height;

		if (!resolvedWidth && !resolvedHeight) {
			resolvedWidth = image.width;
			resolvedHeight = image.height;
		} else if (resolvedWidth && !resolvedHeight) {
			resolvedHeight = Math.round(resolvedWidth / aspectRatio);
		} else if (!resolvedWidth && resolvedHeight) {
			resolvedWidth = Math.round(resolvedHeight * aspectRatio);
		}

		const { lightModeSrcSet, darkModeSrcSet } = generateSrcSet(image);

		if (!image.hasDarkMode) {
			return (
				<img
					loading={loading}
					width={resolvedWidth}
					height={resolvedHeight}
					alt={alt}
					srcSet={lightModeSrcSet}
					src={image.uri}
					ref={ref}
					className={className}
					{...rest}
				/>
			);
		}

		return (
			<>
				<img
					loading={loading}
					className={classNames(className, 'hidden dark:inline')}
					width={resolvedWidth}
					height={resolvedHeight}
					alt={alt}
					srcSet={darkModeSrcSet}
					src={image.darkModeUri}
					ref={ref}
					{...rest}
				/>
				<img
					className={classNames(className, 'inline dark:hidden')}
					loading={loading}
					width={resolvedWidth}
					height={resolvedHeight}
					alt={alt}
					srcSet={lightModeSrcSet}
					src={image.uri}
					ref={ref}
					{...rest}
				/>
			</>
		);
	},
);

export { ImageToken };
